import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { fetchAppConfig, type AppConfigKey } from '@/api/console';
import {
    Callout,
    DataTable,
    EmptyState,
    ErrorState,
    HoverCard,
    SectionCard,
    SkeletonTable,
    type ColumnConfig,
} from '@/components/ui';
import { displayValue } from '../Config/configDisplay';
import type { AppDetailPageVM } from './useAppDetailPage';
import styles from './AppDetailConfigTab.module.css';

/**
 * EAA-1823: tab "Cấu hình" of `/console/apps/:code` — the configuration the
 * SERVICE is actually running with, not the portal's configuration about it.
 *
 * Read-only in this pass. The write branch reuses `/console/config`'s two-step
 * stage/confirm (`Config/EffectiveConfigPanel.tsx`) and ships separately, which
 * is why nothing here stages anything and `spec` goes unused for now.
 *
 * Reuse vs `/console/config`: this shares `configDisplay`'s `displayValue` (so a
 * duration reads the same in both places) but NOT `EffectiveConfigPanel` itself.
 * That panel is bound to portal-api's three-map shape and its edit flow; a
 * per-app service returns one array with metadata per key. Bending the panel to
 * both shapes would have meant rewriting the screen that already works, for a
 * read-only table — so the presentation is shared and the data layer is not.
 */
export function AppDetailConfigTab({ vm }: { vm: AppDetailPageVM }) {
    const { t, i18n } = useTranslation();
    const can = useCan();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();

    const canViewConfig = can('console.config.view');

    // EAA-1842 rule: a read whose answer depends on ambient scope puts that scope
    // IN the query key and stays disabled until it resolves. `code` comes from the
    // route, so asking before it exists would cache another app's config under
    // this app's row.
    const configQuery = useQuery({
        queryKey: ['app-config', vm.code, accessToken],
        queryFn: () => fetchAppConfig(config.apiBase, accessToken as string, vm.code),
        enabled: Boolean(vm.code) && authenticated && Boolean(accessToken) && canViewConfig,
        retry: false,
    });

    const columns: ColumnConfig<AppConfigKey>[] = useMemo(
        () => [
            {
                key: 'key',
                title: t('console.appDetail.config.columnKey'),
                dataIndex: 'key',
                render: (_value, row) => (
                    <span className={styles.name}>
                        {/* The service supplies the label; the raw key stays reachable
                            because that is what an operator greps for in settings. */}
                        {row.label || row.key}
                        <HoverCard
                            placement="right"
                            trigger={
                                <span className={styles.info} aria-label={row.key} role="img">
                                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
                                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                                        <path
                                            d="M12 11v5M12 7.6h.01"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                </span>
                            }
                            content={
                                <div className={styles.tip}>
                                    <code>{row.key}</code>
                                    {row.description && <p className={styles.tipText}>{row.description}</p>}
                                </div>
                            }
                        />
                    </span>
                ),
            },
            {
                key: 'value',
                title: t('console.appDetail.config.columnValue'),
                dataIndex: 'value',
                render: (_value, row) => {
                    // Secrets never render a value, not even a masked one — the last
                    // four characters are still a leak and help nothing on this screen.
                    // PENDING (backlog item I): once the service is allowed to report
                    // `is_set`, this becomes "Đã đặt" / "Chưa đặt". Until then the row
                    // exists (so the key is discoverable) but says only that it is hidden.
                    if (row.is_secret) {
                        return <span className={styles.secret}>{t('console.appDetail.config.secretHidden')}</span>;
                    }

                    return (
                        <>
                            <span className={styles.value}>{displayValue(row.key, row.value, t)}</span>
                            {/* A default equal to the value tells nobody anything, so it
                                only shows on rows that actually moved. */}
                            {row.source !== 'default' && (
                                <span className={styles.provenance}>
                                    {t('console.appDetail.config.changedFrom', {
                                        value: displayValue(row.key, row.default, t),
                                    })}
                                    {row.updated_at
                                        ? ` · ${new Date(row.updated_at).toLocaleString(i18n.language)}`
                                        : ''}
                                </span>
                            )}
                        </>
                    );
                },
            },
            {
                key: 'source',
                title: t('console.appDetail.config.columnSource'),
                dataIndex: 'source',
                render: (value) => (
                    <span className={`${styles.sourcePill} ${styles[`source_${value as string}`] ?? ''}`}>
                        {t(`console.appDetail.config.source.${value as string}`, {
                            defaultValue: value as string,
                        })}
                    </span>
                ),
            },
            {
                key: 'flags',
                title: t('console.appDetail.config.columnFlags'),
                dataIndex: 'requires_restart',
                render: (_value, row) => (
                    <span className={styles.flags}>
                        {row.editable ? (
                            <span className={styles.flagEditable}>
                                {t('console.appDetail.config.flagEditable')}
                            </span>
                        ) : (
                            <span className={styles.flagReadOnly}>
                                {t('console.appDetail.config.flagReadOnly')}
                            </span>
                        )}
                        {row.requires_restart && (
                            <span className={styles.flagRestart}>
                                {t('console.appDetail.config.flagRestart')}
                            </span>
                        )}
                    </span>
                ),
            },
        ],
        [t, i18n.language]
    );

    if (!canViewConfig) {
        return (
            <SectionCard>
                <p className="portal-admin-hint">{t('console.appDetail.config.accessDenied')}</p>
            </SectionCard>
        );
    }

    // A service that says it exposes no config is an ANSWER, not a failure — the
    // brief's "chưa hỗ trợ" state. Distinct from the error branch below, which is
    // what an operator has to act on.
    if (configQuery.isSuccess && !configQuery.data.supported) {
        return (
            <SectionCard>
                <EmptyState
                    title={t('console.appDetail.config.unsupportedTitle')}
                    description={t('console.appDetail.config.unsupportedDescription')}
                />
            </SectionCard>
        );
    }

    const rows = configQuery.data?.keys ?? [];

    return (
        <SectionCard bodyPadded={false}>
            {configQuery.isLoading && <SkeletonTable rows={8} columns={4} />}

            {configQuery.isError && (
                <div className={styles.errorWrap}>
                    <ErrorState
                        description={t('console.appDetail.config.loadError')}
                        onRetry={() => configQuery.refetch()}
                        retrying={configQuery.isFetching}
                    />
                    {/* Said out loud rather than shown as a generic failure: until the
                        proxy ships, this tab CANNOT succeed, and an operator should not
                        spend time checking their own permissions over it. */}
                    <Callout tone="info" label={t('console.appDetail.config.pendingApiLabel')} longLabel>
                        {t('console.appDetail.config.pendingApiBody', { code: vm.code })}
                    </Callout>
                </div>
            )}

            {configQuery.isSuccess && rows.length === 0 && (
                <EmptyState
                    title={t('console.appDetail.config.emptyTitle')}
                    description={t('console.appDetail.config.emptyDescription')}
                />
            )}

            {configQuery.isSuccess && rows.length > 0 && (
                <div className="portal-table-scroll">
                    <DataTable<AppConfigKey>
                        data={rows}
                        columns={columns}
                        rowKey="key"
                        pagination={false}
                    />
                </div>
            )}
        </SectionCard>
    );
}

export default AppDetailConfigTab;
